import * as React from 'react';
import { BookOpen, Search, Plus, BarChart2, Activity, Sparkles, FolderOpen, User, Shield, Bookmark, Bug } from 'lucide-react';
import { ThemeToggle } from '../theme-toggle';
import { cn } from '../../lib/utils';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { appConfig } from '../../config/appConfig';

interface SidebarProps {
  currentView: 'browse' | 'submit' | 'analytics' | 'categories' | 'myPrompts' | 'moderation' | 'savedSearches';
  setCurrentView: (v: 'browse' | 'submit' | 'analytics' | 'categories' | 'myPrompts' | 'moderation' | 'savedSearches') => void;
  onNavigationClick?: () => void;
  mobileOpen?: boolean;
}

export function Sidebar({ currentView, setCurrentView, onNavigationClick, mobileOpen }: SidebarProps) {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  
  // Get authenticated user info if available
  const account = accounts[0];
  const displayName = account?.name || account?.username || 'User';
  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Debug logging
  React.useEffect(() => {
    console.log('[Sidebar] enableAuth:', appConfig.enableAuthentication, 
                'isAuthenticated:', isAuthenticated,
                'accounts:', accounts.length,
                'account:', account ? { name: account.name, username: account.username } : null);
  }, [isAuthenticated, accounts, account]);

  return (
    <aside
      className={cn(
        'w-68 shrink-0 flex-col bg-surface-alt/95 backdrop-blur border-r border-border-subtle/60 text-foreground/90',
        mobileOpen ? 'flex absolute inset-y-0 left-0 z-50 shadow-xl' : 'hidden md:flex'
      )}
      aria-label="Primary"
    >
      {/* Brand/Header */}
      <div className="px-5 pt-4 pb-3 border-b border-border-subtle/70">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center text-primary">
              <BookOpen className="w-5 h-5" aria-hidden="true" />
            </div>
            <div className="leading-tight">
              <h1 className="font-semibold text-sm tracking-wide" id="app-title">AI Prompt Library</h1>
              <p className="text-[11px] text-muted-foreground" aria-describedby="app-title">Repository</p>
            </div>
          </div>
          <ThemeToggle aria-label="Toggle theme" />
        </div>
      </div>

      {/* User - Only show when authentication is enabled and user is authenticated */}
      {appConfig.enableAuthentication && isAuthenticated && account && (
        <div className="px-5 py-4 border-b border-border-subtle/70">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-sm font-medium text-primary select-none">
              {initials}
            </div>
            <div className="min-w-0 leading-tight">
              <p className="text-sm font-medium truncate">{displayName}</p>
              <p className="text-[11px] text-muted-foreground">{account.username}</p>
            </div>
            <span className="ml-auto h-2.5 w-2.5 rounded-full bg-success" aria-label="Status online" />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto" aria-label="Main navigation">
        <ul className="space-y-1">
          {[
            { id: 'browse', label: 'Prompt Repository', icon: Search },
            { id: 'submit', label: 'Submit Prompt', icon: Plus },
            ...(appConfig.enableAuthentication && isAuthenticated ? [{ id: 'myPrompts', label: 'My Prompts', icon: User }] : []),
            ...(appConfig.enableAuthentication && isAuthenticated ? [{ id: 'savedSearches', label: 'Saved Searches', icon: Bookmark }] : []),
            { id: 'categories', label: 'Category Management', icon: FolderOpen },
            { id: 'analytics', label: 'Analytics', icon: BarChart2 },
            ...(appConfig.enableAuthentication && isAuthenticated ? [{ id: 'moderation', label: 'Review Moderation', icon: Shield }] : [])
          ].map(item => {
            const active = currentView === item.id;
            const Icon = item.icon;
            return (
              <li key={item.id}>
                <button
                  onClick={() => {
                    setCurrentView(item.id as any);
                    onNavigationClick?.();
                  }}
                  className={cn(
                    'group relative w-full flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    active
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-surface-accent'
                  )}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  <span className="truncate">{item.label}</span>
                  {active && (
                    <span className="absolute inset-y-0 right-0 w-1 rounded-r-md bg-primary-foreground/30" aria-hidden="true" />
                  )}
                </button>
              </li>
            );
          })}
          <li>
            <a
              href="mailto:bjshearer@bluemountain.io?subject=Bug%20Report%20-%20AI%20Prompt%20Library"
              onClick={() => onNavigationClick?.()}
              className="group relative w-full flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring text-muted-foreground hover:text-foreground hover:bg-surface-accent"
            >
              <Bug className="w-4 h-4" aria-hidden="true" />
              <span className="truncate">Report Bug</span>
            </a>
          </li>
        </ul>
        <div className="mt-8 px-2">
          <div className="rounded-lg border border-border-subtle/70 bg-surface shadow-xs p-3">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
              <Activity className="w-3.5 h-3.5" /> System Status
            </div>
            <ul className="space-y-1 text-[11px] text-muted-foreground/90">
              <li className="flex items-center justify-between"><span>API</span><span className="inline-flex items-center gap-1 text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" />Online</span></li>
              <li className="flex items-center justify-between"><span>Web</span><span className="inline-flex items-center gap-1 text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" />Healthy</span></li>
              <li className="flex items-center justify-between"><span>Mode</span><span className="text-foreground/70">{process.env.NODE_ENV}</span></li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-border-subtle/70 text-[11px] text-muted-foreground bg-surface-accent/60">
        <p className="flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
          <span className="truncate">Built with AI</span>
        </p>
      </div>
    </aside>
  );
}
